/**
 * Verorten nach Reise statt nach Platz.
 *
 * Von den 386 Plätzen ohne Koordinaten hängen **182 an zwölf Reisen**, die
 * daneben schon verortete Plätze haben (gemessen am Bestand, 24.08.2026). Das
 * ist die grosse Hälfte der Arbeit, und sie zerfällt in zwölf Sitzungen statt
 * in 182 Einzelsuchen: Wer die Karte der Reise einmal vor sich hat, setzt die
 * fehlenden Nadeln in derselben Gegend nacheinander, ohne den Ausschnitt
 * zwischendurch zu verlieren.
 *
 * **Die Reihenfolge der Tauchgänge ist die Route.** Ein Liveaboard fährt sie
 * ab; wer die Tage vor sich hat, erkennt an den verorteten Nachbarn, wo die
 * Lücke dazwischen liegt. Deshalb ist der Reiseplan nach Tagen gegliedert und
 * nicht alphabetisch.
 *
 * **Gerechnet wird daraus nichts.** Ein erster Entwurf wollte die Mitte
 * zwischen dem verorteten Tauchgang davor und dem danach als Vorschlag setzen.
 * An den echten Daten liegen diese beiden im Median **90 km** auseinander
 * (75 % bis 176 km) — die Mitte läge meist im offenen Wasser. Was bleibt, ist
 * der Tagesanker: Plätze desselben Tages streuen im Median **25 km**. Das ist
 * ein Hinweis, keine Position, und wird auch so angeboten.
 */

import { ortszeit } from './format.js';

/**
 * Der **Ortstag** eines Tauchgangs, nicht der UTC-Tag.
 *
 * `start_utc` roh abzuschneiden wäre der Fehler, vor dem `format.js` warnt: Ein
 * Tauchgang um 08:00 in Japan steht als 23:00 des Vortags in UTC. Am Bestand
 * sind es 28 von 995 Tauchgängen, die dabei in den falschen Tag rutschten —
 * genug, um eine Tagesfolge unlesbar und den Tagesanker falsch zu machen.
 */
function tagVon(tg) {
  const zeit = ortszeit(tg);
  if (!zeit) return '';
  return `${zeit.getUTCFullYear()}-${String(zeit.getUTCMonth() + 1).padStart(2, '0')}`
    + `-${String(zeit.getUTCDate()).padStart(2, '0')}`;
}

/** Ortszeit als sortierbarer Zeitstempel — dieselbe Rechnung wie der Tag. */
const zeitVon = (tg) => ortszeit(tg)?.getTime() ?? 0;

const hatKoordinaten = (platz) => typeof platz?.lat === 'number' && typeof platz?.lon === 'number';

/**
 * Die Reisen, an denen noch Arbeit hängt — die meiste zuerst.
 *
 * Reisen ohne offenen Platz fallen heraus; sie stehen im Weg, nicht zur
 * Auswahl. Reisen ganz ohne verorteten Platz bleiben drin, aber sie sagen es:
 * Dort hilft die Nachbarschaft nicht, weil es keine gibt.
 *
 * `nurOffene: false` gibt alle zurück. Die Ansicht braucht das, um eine Reise,
 * die sie gerade fertig gemacht hat, nicht mitten in der Arbeit daran aus der
 * Auswahl zu verlieren.
 */
export function reiseListe(plaetze, tauchgaenge, { nurOffene = true } = {}) {
  const nachId = new Map(plaetze.map((p) => [p.id, p]));
  const reisen = new Map();

  for (const tg of tauchgaenge) {
    if (!tg.trip_id || !tg.site_id) continue;
    if (!reisen.has(tg.trip_id)) {
      reisen.set(tg.trip_id, {
        id: tg.trip_id,
        name: tg.trip_name ?? 'ohne Namen',
        plaetze: new Set(),
        tage: new Set(),
      });
    }
    const reise = reisen.get(tg.trip_id);
    reise.plaetze.add(tg.site_id);
    const tag = tagVon(tg);
    if (tag) reise.tage.add(tag);
  }

  return [...reisen.values()]
    .map((reise) => {
      const saetze = [...reise.plaetze].map((id) => nachId.get(id)).filter(Boolean);
      const tage = [...reise.tage].sort();
      return {
        id: reise.id,
        name: reise.name,
        offen: saetze.filter((p) => !hatKoordinaten(p)).length,
        verortet: saetze.filter(hatKoordinaten).length,
        von: tage[0] ?? null,
        bis: tage[tage.length - 1] ?? null,
      };
    })
    .filter((reise) => !nurOffene || reise.offen > 0)
    // Reisen mit verortetem Nachbarn zuerst: Dort trägt der Modus, weil es
    // etwas anzupeilen gibt. "2008 - Fiji" hat 30 offene Plätze und keinen
    // einzigen Anker — die meiste Arbeit, aber die geringste Hilfe, und als
    // Einstieg genau der falsche Ort.
    .sort((a, b) => Number(b.verortet > 0) - Number(a.verortet > 0)
      || b.offen - a.offen
      || a.name.localeCompare(b.name, 'de'));
}

/**
 * Eine Reise als Tagesfolge — genau so, wie sie getaucht wurde.
 *
 * Je Tag stehen die Plätze in der Reihenfolge der Tauchgänge, offene und
 * verortete gemischt. Getrennt wären sie leichter zu zählen, aber schwerer zu
 * lesen: Der verortete Platz zwischen zwei offenen ist die eigentliche
 * Auskunft.
 */
export function reiseplan(reiseId, plaetze, tauchgaenge) {
  const nachId = new Map(plaetze.map((p) => [p.id, p]));
  const eigene = tauchgaenge
    .filter((tg) => tg.trip_id === reiseId && tg.site_id)
    .sort((a, b) => zeitVon(a) - zeitVon(b)
      || String(a.id ?? '').localeCompare(String(b.id ?? '')));

  const tage = new Map();
  for (const tg of eigene) {
    const tag = tagVon(tg) || 'ohne Datum';
    if (!tage.has(tag)) tage.set(tag, { tag, eintraege: [], nachPlatz: new Map() });
    const eintrag = tage.get(tag);
    // Ein Platz kann am selben Tag zweimal betaucht sein — eine Zeile, zwei
    // Nummern. Zwei Zeilen läsen sich wie zwei Orte.
    if (!eintrag.nachPlatz.has(tg.site_id)) {
      const platz = nachId.get(tg.site_id);
      if (!platz) continue;
      const zeile = { platz, verortet: hatKoordinaten(platz), nummern: [] };
      eintrag.nachPlatz.set(tg.site_id, zeile);
      eintrag.eintraege.push(zeile);
    }
    const nummer = tg.dive_number;
    if (nummer !== null && nummer !== undefined) {
      eintrag.nachPlatz.get(tg.site_id).nummern.push(nummer);
    }
  }

  return [...tage.values()]
    .map(({ tag, eintraege }) => ({ tag, eintraege }))
    .sort((a, b) => a.tag.localeCompare(b.tag));
}

/**
 * Verortete Plätze, an denen am selben Tag getaucht wurde.
 *
 * Der stärkste Hinweis, den die Reise hergibt: Wer morgens 25 km entfernt im
 * Wasser war, war nachmittags nicht auf der anderen Seite des Archipels. Über
 * die Reise hinweg zählt das nicht mehr — dort sind es im Median 90 km.
 *
 * Der Tag zählt, nicht die Reise: Ein Tag gehört ohnehin zu genau einer, und
 * so greift die Regel auch bei Tauchgängen, die keiner Reise zugeordnet sind.
 */
export function tagesanker(platz, plaetze, tauchgaenge) {
  if (!platz) return [];
  const meineTage = new Set(tauchgaenge
    .filter((tg) => tg.site_id === platz.id)
    .map(tagVon)
    .filter(Boolean));
  if (meineTage.size === 0) return [];

  const nachId = new Map(plaetze.map((p) => [p.id, p]));
  const gefunden = new Map();
  for (const tg of tauchgaenge) {
    if (tg.site_id === platz.id || !tg.site_id) continue;
    const tag = tagVon(tg);
    if (!tag || !meineTage.has(tag)) continue;
    const kandidat = nachId.get(tg.site_id);
    if (!kandidat || !hatKoordinaten(kandidat) || gefunden.has(kandidat.id)) continue;
    gefunden.set(kandidat.id, {
      id: kandidat.id, name: kandidat.name ?? 'ohne Namen', lat: kandidat.lat, lon: kandidat.lon, tag,
    });
  }
  return [...gefunden.values()].sort((a, b) => a.tag.localeCompare(b.tag));
}
